import { useState } from "react";
import { AppNav } from "@/components/AppNav";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { thaiBudget } from "@/lib/budgetThaiData";
import { ukBudget } from "@/lib/budgetUkData";
import ExecBoardSummary from "@/components/budget/ExecBoardSummary";

const THAI_COMPS = [
  { key: "ocean", name: "Ocean Freight", color: "#0b3d59" },
  { key: "baf", name: "BAF (bunker adj.)", color: "#1c7293" },
  { key: "pss", name: "PSS (peak season)", color: "#e0a458" },
  { key: "ebs", name: "EBS (emergency bunker)", color: "#c5372f" },
];

const UK_COMPS = [
  { key: "ocean", name: "Ocean Freight", color: "#1c7293" },
  { key: "baf", name: "BAF (bunker adj.)", color: "#e0a458" },
];

const money = (v) => (v == null ? "—" : `US$${Number(v).toLocaleString()}`);

function ChartTooltip({ active, payload, comps, marketName }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs" data-testid="budget-tooltip">
      <div className="font-semibold text-slate-900 mb-1.5">{row.dateLabel}</div>
      {comps.map((c) => (
        <div key={c.key} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}</span>
          <span className="font-num text-slate-700">{money(row[c.key])}</span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-4 border-t border-slate-100 mt-1.5 pt-1.5 font-semibold">
        <span>Total NAC build-up</span><span className="font-num">{money(row.total)}</span>
      </div>
      {marketName && row.xeneta != null && (
        <div className="flex items-center justify-between gap-4 mt-1">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-0.5" style={{ background: "#9333ea" }} />{marketName}</span>
          <span className="font-num text-purple-700">{money(row.xeneta)}</span>
        </div>
      )}
    </div>
  );
}

function SummaryBlock({ summary, reasonsPrefix }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "2025 Avg Spend NAC", v: money(summary.avg2025) },
          { l: "NAC (Apr 2026 – YTD)", v: money(summary.ytd2026) },
          { l: "Increase YoY", v: summary.change, accent: true },
          { l: "2027–Mar 2028 Budget Est.", v: money(summary.budget2728), accent: true },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl border p-4 ${s.accent ? "border-orange-200 bg-orange-50/60" : "border-slate-200 bg-white"}`} data-testid={`budget-kpi-${i}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{s.l}</div>
            <div className={`font-num text-2xl font-bold mt-1 ${s.accent ? "text-orange-600" : "text-slate-900"}`}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        <span className="font-semibold text-slate-800">{reasonsPrefix} </span>{summary.reasons}. The chart below bifurcates each cost element so the drivers of the increase are visible over time.
      </div>
    </>
  );
}

function BudgetChart({ rows, comps, title, subtitle, marketName }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={28} />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
          <Tooltip content={<ChartTooltip comps={comps} marketName={marketName} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {comps.map((c) => (
            <Area key={c.key} type="stepAfter" dataKey={c.key} name={c.name} stackId="1"
              stroke={c.color} fill={c.color} fillOpacity={0.85} />
          ))}
          <Line type="stepAfter" dataKey="total" name="Total NAC build-up" stroke="#0f172a" strokeWidth={2} dot={false} />
          {marketName && (
            <Line type="monotone" dataKey="xeneta" name={marketName} stroke="#9333ea" strokeWidth={1.75} strokeDasharray="5 3" dot={false} connectNulls />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ThailandView() {
  const { rows, summary } = thaiBudget;
  return (
    <div className="space-y-8" data-testid="budget-thailand">
      <SummaryBlock summary={summary} reasonsPrefix="Why the ~30% increase into 2027–28:" />
      <BudgetChart
        rows={rows}
        comps={THAI_COMPS}
        title="Thailand → Australia — cost build-up per FEU (NAC)"
        subtitle="Weekly, Apr 2025 – Mar 2028. Each band stacks to the total MNA NAC (EBS introduced, BAF/PSS stepped up); the dashed line is the Xeneta market rate (LCH–SYD) for reference — US$/FEU."
        marketName="Xeneta market (LCH–SYD)"
      />
    </div>
  );
}

function UKView() {
  const { rows, summary } = ukBudget;
  return (
    <div className="space-y-8" data-testid="budget-uk">
      <SummaryBlock summary={summary} reasonsPrefix="Why the increase into 2027–28:" />
      <BudgetChart
        rows={rows}
        comps={UK_COMPS}
        title="UK → Australia — cost build-up per FEU (NAC)"
        subtitle="Weekly, Apr 2025 – Mar 2028. Each band stacks to the total NAC build-up (Ocean + BAF, excluding origin pick-up); the dashed line is the Xeneta market rate (SOU–SYD RH40) for reference — US$/FEU."
        marketName="Xeneta market (SOU–SYD)"
      />
    </div>
  );
}

export default function BudgetAssumptions() {
  const [tab, setTab] = useState("board");
  return (
    <div className="min-h-screen bg-slate-50">
      <AppNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="font-num text-xs uppercase tracking-[0.18em] text-orange-600">Procurement Planning</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">Budget Assumptions 2027–2028</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Forward budget build-up by lane, with each cost element bifurcated so the drivers behind the year-on-year increase are transparent.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {[{ k: "board", l: "Board Summary" }, { k: "thailand", l: "Thailand → Australia" }, { k: "uk", l: "UK → Australia" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`budget-tab-${t.k}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:text-slate-900"}`}>
              {t.l}
            </button>
          ))}
        </div>
        {tab === "board" ? <ExecBoardSummary /> : tab === "thailand" ? <ThailandView /> : <UKView />}
      </div>
    </div>
  );
}
